'use client'

// Awards cinematic — directional panel choreography.
//
// TIMELINE (scrub 0.5, pin +=400%, normalized 0→1)
//   0.00–0.08   intro items rise in
//   0.12        scan → act01   panels enter
//   0.14–0.22   act01 left (numeral) ← from -vw, right (photo) → from +vw
//   0.22–0.30   act01 text stagger, stamp drops
//   0.36        scan → act02   act01 panels exit
//   0.38–0.46   act02 left (photo) ← from -vw, right (numeral) → from +vw
//   0.46–0.54   act02 text stagger, stamp drops
//   0.58        scan → act03   act02 panels exit
//   0.60–0.68   act03 top (numeral) from -vh, bottom (photo) from +vh
//   0.68–0.76   act03 text stagger, stamp drops
//   0.80        scan → close   act03 panels exit
//   0.84–0.92   close items rise in

import { RefObject } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from './gsapSetup'
import { useReducedMotion } from './useReducedMotion'

export function useAwardsCinematic(ref: RefObject<HTMLElement | null>) {
  const reduced = useReducedMotion()

  useGSAP(
    () => {
      gsapSetup()

      const root = ref.current
      if (!root) return

      const viewport = root.querySelector<HTMLElement>('[data-aw-viewport]')
      const scan     = root.querySelector<HTMLElement>('[data-aw-scan]')
      if (!viewport) return

      const isTouch =
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 767px), (hover: none) and (pointer: coarse)').matches

      // ── Reduced motion / mobile: simple scroll reveals ─────────────────
      if (reduced || isTouch) {
        const acts = gsap.utils.toArray<HTMLElement>('[data-aw-act]', root)
        acts.forEach((act) => {
          gsap.set(act, { position: 'relative', autoAlpha: 1, overflow: 'visible' })
          const items = gsap.utils.toArray<HTMLElement>('[data-aw-item], [data-aw-title]', act)
          if (items.length === 0) return
          gsap.set(items, { autoAlpha: 0, y: 20 })
          gsap.to(items, {
            autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.06,
            scrollTrigger: { trigger: act, start: 'top 78%', toggleActions: 'play none none reverse' },
          })
        })
        return
      }

      // ── Helpers ────────────────────────────────────────────────────────
      const vw = () => window.innerWidth
      const vh = () => window.innerHeight

      const act = (id: string) => root.querySelector<HTMLElement>(`[data-aw-act="${id}"]`)
      const panel = (actEl: HTMLElement | null, side: string) =>
        actEl?.querySelector<HTMLElement>(`[data-aw-panel="${side}"]`) ?? null
      const items = (actEl: HTMLElement | null) =>
        actEl ? gsap.utils.toArray<HTMLElement>('[data-aw-item], [data-aw-title]', actEl) : []
      const stamp = (actEl: HTMLElement | null) =>
        actEl?.querySelector<HTMLElement>('[data-aw-stamp]') ?? null

      const intro  = act('intro')
      const a01    = act('01')
      const a02    = act('02')
      const a03    = act('03')
      const close  = act('close')

      // ── Initial states ─────────────────────────────────────────────────
      // Panels are positioned off-screen; acts are visible (panels clip them)
      gsap.set([intro, a01, a02, a03, close].filter(Boolean), { autoAlpha: 1 })

      // Intro text hidden, will stagger in
      if (intro) gsap.set(items(intro), { autoAlpha: 0, y: 22 })

      // Act 01: horizontal, numeral LEFT, photo RIGHT
      if (a01) {
        gsap.set(panel(a01, 'left'),  { x: () => -vw() })
        gsap.set(panel(a01, 'right'), { x: () =>  vw() })
        gsap.set(items(a01), { autoAlpha: 0, y: 18 })
        const s = stamp(a01)
        const rot = Number(s?.dataset.stampRot ?? 7)
        if (s) gsap.set(s, { autoAlpha: 0, scale: 1.25, rotate: rot - 28, y: -18 })
      }

      // Act 02: horizontal, photo LEFT, numeral RIGHT
      if (a02) {
        gsap.set(panel(a02, 'left'),  { x: () => -vw() })
        gsap.set(panel(a02, 'right'), { x: () =>  vw() })
        gsap.set(items(a02), { autoAlpha: 0, y: 18 })
        const s = stamp(a02)
        const rot = Number(s?.dataset.stampRot ?? 7)
        if (s) gsap.set(s, { autoAlpha: 0, scale: 1.25, rotate: rot - 28, y: -18 })
      }

      // Act 03: vertical, numeral TOP, photo BOTTOM
      if (a03) {
        gsap.set(panel(a03, 'top'),    { y: () => -vh() })
        gsap.set(panel(a03, 'bottom'), { y: () =>  vh() })
        gsap.set(items(a03), { autoAlpha: 0, y: 18 })
        const s = stamp(a03)
        const rot = Number(s?.dataset.stampRot ?? 7)
        if (s) gsap.set(s, { autoAlpha: 0, scale: 1.25, rotate: rot - 28, y: -18 })
      }

      // Close text hidden
      if (close) gsap.set(items(close), { autoAlpha: 0, y: 22 })

      // Scan line
      if (scan) gsap.set(scan, { autoAlpha: 0, y: '0%' })

      // ── Master timeline ────────────────────────────────────────────────
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger:  viewport,
          start:    'top top',
          end:      '+=400%',
          pin:      true,
          pinSpacing: true,
          scrub:    0.5,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onRefresh: () => {
            // Re-apply off-screen positions after reflow
            if (a01) {
              gsap.set(panel(a01, 'left'),  { x: -vw() })
              gsap.set(panel(a01, 'right'), { x:  vw() })
            }
            if (a02) {
              gsap.set(panel(a02, 'left'),  { x: -vw() })
              gsap.set(panel(a02, 'right'), { x:  vw() })
            }
            if (a03) {
              gsap.set(panel(a03, 'top'),    { y: -vh() })
              gsap.set(panel(a03, 'bottom'), { y:  vh() })
            }
          },
        },
      })

      // ── SCAN helper — sweeps at position `at` ──────────────────────────
      const SWEEP = 0.05
      const addScan = (at: number) => {
        if (!scan) return
        tl.to(scan, { autoAlpha: 1, duration: 0.01 }, at)
        tl.fromTo(scan, { y: '-2%' }, { y: '102%', duration: SWEEP, ease: 'power2.inOut' }, at)
        tl.to(scan, { autoAlpha: 0, duration: 0.02 }, at + SWEEP - 0.02)
      }

      // ── INTRO enter (0.00–0.08) ────────────────────────────────────────
      tl.to(items(intro), {
        autoAlpha: 1, y: 0, duration: 0.07, ease: 'power2.out', stagger: 0.014,
      }, 0.00)

      // ── INTRO → ACT 01 (scan at 0.12) ─────────────────────────────────
      addScan(0.12)
      // Fade intro out under scan
      tl.to(items(intro), { autoAlpha: 0, duration: 0.04, ease: 'power2.in' }, 0.12)

      // ACT 01 panels enter (0.14–0.22)
      tl.to(panel(a01, 'left'),  { x: 0, duration: 0.10, ease: 'power3.out' }, 0.14)
      tl.to(panel(a01, 'right'), { x: 0, duration: 0.10, ease: 'power3.out' }, 0.14)
      // ACT 01 text stagger (0.22–0.30)
      tl.to(items(a01), {
        autoAlpha: 1, y: 0, duration: 0.06, ease: 'power2.out', stagger: 0.011,
      }, 0.22)
      // ACT 01 stamp drop (0.26)
      const s01 = stamp(a01)
      if (s01) {
        const rot01 = Number(s01.dataset.stampRot ?? 7)
        tl.to(s01, { autoAlpha: 1, scale: 1, rotate: rot01, y: 0, duration: 0.09, ease: 'expo.out' }, 0.26)
      }

      // ── ACT 01 → ACT 02 (scan at 0.36) ────────────────────────────────
      addScan(0.36)
      // ACT 01 panels exit (same direction as entry)
      tl.to(panel(a01, 'left'),  { x: () => -vw(), duration: 0.08, ease: 'power3.in' }, 0.35)
      tl.to(panel(a01, 'right'), { x: () =>  vw(), duration: 0.08, ease: 'power3.in' }, 0.35)

      // ACT 02 panels enter (0.38–0.46)
      tl.to(panel(a02, 'left'),  { x: 0, duration: 0.10, ease: 'power3.out' }, 0.38)
      tl.to(panel(a02, 'right'), { x: 0, duration: 0.10, ease: 'power3.out' }, 0.38)
      // ACT 02 text stagger (0.46–0.54)
      tl.to(items(a02), {
        autoAlpha: 1, y: 0, duration: 0.06, ease: 'power2.out', stagger: 0.011,
      }, 0.46)
      // ACT 02 stamp (0.50)
      const s02 = stamp(a02)
      if (s02) {
        const rot02 = Number(s02.dataset.stampRot ?? 7)
        tl.to(s02, { autoAlpha: 1, scale: 1, rotate: rot02, y: 0, duration: 0.09, ease: 'expo.out' }, 0.50)
      }

      // ── ACT 02 → ACT 03 (scan at 0.58) ────────────────────────────────
      addScan(0.58)
      tl.to(panel(a02, 'left'),  { x: () => -vw(), duration: 0.08, ease: 'power3.in' }, 0.57)
      tl.to(panel(a02, 'right'), { x: () =>  vw(), duration: 0.08, ease: 'power3.in' }, 0.57)

      // ACT 03 panels enter (0.60–0.68, vertical)
      tl.to(panel(a03, 'top'),    { y: 0, duration: 0.10, ease: 'power3.out' }, 0.60)
      tl.to(panel(a03, 'bottom'), { y: 0, duration: 0.10, ease: 'power3.out' }, 0.60)
      // ACT 03 text stagger (0.68–0.76)
      tl.to(items(a03), {
        autoAlpha: 1, y: 0, duration: 0.06, ease: 'power2.out', stagger: 0.011,
      }, 0.68)
      // ACT 03 stamp (0.72)
      const s03 = stamp(a03)
      if (s03) {
        const rot03 = Number(s03.dataset.stampRot ?? 7)
        tl.to(s03, { autoAlpha: 1, scale: 1, rotate: rot03, y: 0, duration: 0.09, ease: 'expo.out' }, 0.72)
      }

      // ── ACT 03 → CLOSE (scan at 0.80) ─────────────────────────────────
      addScan(0.80)
      tl.to(panel(a03, 'top'),    { y: () => -vh(), duration: 0.08, ease: 'power3.in' }, 0.79)
      tl.to(panel(a03, 'bottom'), { y: () =>  vh(), duration: 0.08, ease: 'power3.in' }, 0.79)

      // CLOSE items rise in (0.84–0.92)
      tl.to(items(close), {
        autoAlpha: 1, y: 0, duration: 0.07, ease: 'power2.out', stagger: 0.014,
      }, 0.84)

      ScrollTrigger.refresh()
    },
    { scope: ref, dependencies: [reduced], revertOnUpdate: true },
  )
}
